/**
 * Phone Number Formatting and Validation Library
 * VOY-226: Implements country-specific phone formatting and validation
 */

// Country phone configuration with dial codes, formats, and validation
export interface CountryPhoneConfig {
  code: string;        // ISO 3166-1 alpha-2 country code
  dial: string;        // International dialing code (e.g., '+1')
  name: string;        // Country name
  flag: string;        // Emoji flag
  format: string;      // Display format pattern (X = digit)
  placeholder: string; // Input placeholder
  minLength: number;   // Min digits (excluding country code)
  maxLength: number;   // Max digits (excluding country code)
  example: string;     // Example number
}

// Comprehensive list of 60+ countries with phone configurations
export const COUNTRY_PHONE_CONFIGS: CountryPhoneConfig[] = [
  // North America
  { code: 'US', dial: '+1', name: 'United States', flag: '🇺🇸', format: '(XXX) XXX-XXXX', placeholder: '(555) 123-4567', minLength: 10, maxLength: 10, example: '5551234567' },
  { code: 'CA', dial: '+1', name: 'Canada', flag: '🇨🇦', format: '(XXX) XXX-XXXX', placeholder: '(555) 123-4567', minLength: 10, maxLength: 10, example: '5551234567' },
  { code: 'MX', dial: '+52', name: 'Mexico', flag: '🇲🇽', format: 'XX XXXX XXXX', placeholder: '55 1234 5678', minLength: 10, maxLength: 10, example: '5512345678' },
  
  // South America
  { code: 'AR', dial: '+54', name: 'Argentina', flag: '🇦🇷', format: 'XX XXXX-XXXX', placeholder: '11 1234-5678', minLength: 10, maxLength: 10, example: '1112345678' },
  { code: 'BR', dial: '+55', name: 'Brazil', flag: '🇧🇷', format: '(XX) XXXXX-XXXX', placeholder: '(11) 91234-5678', minLength: 10, maxLength: 11, example: '11912345678' },
  { code: 'CL', dial: '+56', name: 'Chile', flag: '🇨🇱', format: 'X XXXX XXXX', placeholder: '9 1234 5678', minLength: 9, maxLength: 9, example: '912345678' },
  { code: 'CO', dial: '+57', name: 'Colombia', flag: '🇨🇴', format: 'XXX XXX XXXX', placeholder: '300 123 4567', minLength: 10, maxLength: 10, example: '3001234567' },
  { code: 'PE', dial: '+51', name: 'Peru', flag: '🇵🇪', format: 'XXX XXX XXX', placeholder: '912 345 678', minLength: 9, maxLength: 9, example: '912345678' },
  { code: 'VE', dial: '+58', name: 'Venezuela', flag: '🇻🇪', format: 'XXX-XXX-XXXX', placeholder: '412-123-4567', minLength: 10, maxLength: 10, example: '4121234567' },
  { code: 'EC', dial: '+593', name: 'Ecuador', flag: '🇪🇨', format: 'XX XXX XXXX', placeholder: '99 123 4567', minLength: 9, maxLength: 9, example: '991234567' },
  { code: 'UY', dial: '+598', name: 'Uruguay', flag: '🇺🇾', format: 'XX XXX XXX', placeholder: '99 123 456', minLength: 8, maxLength: 8, example: '99123456' },
  { code: 'PY', dial: '+595', name: 'Paraguay', flag: '🇵🇾', format: 'XXX XXX XXX', placeholder: '981 123 456', minLength: 9, maxLength: 9, example: '981123456' },
  { code: 'BO', dial: '+591', name: 'Bolivia', flag: '🇧🇴', format: 'XXXX XXXX', placeholder: '7123 4567', minLength: 8, maxLength: 8, example: '71234567' },
  
  // Europe
  { code: 'GB', dial: '+44', name: 'United Kingdom', flag: '🇬🇧', format: 'XXXX XXX XXXX', placeholder: '7911 123 4567', minLength: 10, maxLength: 10, example: '7911123456' },
  { code: 'DE', dial: '+49', name: 'Germany', flag: '🇩🇪', format: 'XXXX XXXXXXX', placeholder: '1512 3456789', minLength: 10, maxLength: 11, example: '15123456789' },
  { code: 'FR', dial: '+33', name: 'France', flag: '🇫🇷', format: 'X XX XX XX XX', placeholder: '6 12 34 56 78', minLength: 9, maxLength: 9, example: '612345678' },
  { code: 'IT', dial: '+39', name: 'Italy', flag: '🇮🇹', format: 'XXX XXX XXXX', placeholder: '312 345 6789', minLength: 9, maxLength: 10, example: '3123456789' },
  { code: 'ES', dial: '+34', name: 'Spain', flag: '🇪🇸', format: 'XXX XX XX XX', placeholder: '612 34 56 78', minLength: 9, maxLength: 9, example: '612345678' },
  { code: 'PT', dial: '+351', name: 'Portugal', flag: '🇵🇹', format: 'XXX XXX XXX', placeholder: '912 345 678', minLength: 9, maxLength: 9, example: '912345678' },
  { code: 'NL', dial: '+31', name: 'Netherlands', flag: '🇳🇱', format: 'X XX XX XX XX', placeholder: '6 12 34 56 78', minLength: 9, maxLength: 9, example: '612345678' },
  { code: 'BE', dial: '+32', name: 'Belgium', flag: '🇧🇪', format: 'XXX XX XX XX', placeholder: '470 12 34 56', minLength: 9, maxLength: 9, example: '470123456' },
  { code: 'AT', dial: '+43', name: 'Austria', flag: '🇦🇹', format: 'XXX XXXXXXX', placeholder: '664 1234567', minLength: 10, maxLength: 11, example: '6641234567' },
  { code: 'CH', dial: '+41', name: 'Switzerland', flag: '🇨🇭', format: 'XX XXX XX XX', placeholder: '78 123 45 67', minLength: 9, maxLength: 9, example: '781234567' },
  { code: 'SE', dial: '+46', name: 'Sweden', flag: '🇸🇪', format: 'XX-XXX XX XX', placeholder: '70-123 45 67', minLength: 9, maxLength: 9, example: '701234567' },
  { code: 'NO', dial: '+47', name: 'Norway', flag: '🇳🇴', format: 'XXX XX XXX', placeholder: '412 34 567', minLength: 8, maxLength: 8, example: '41234567' },
  { code: 'DK', dial: '+45', name: 'Denmark', flag: '🇩🇰', format: 'XX XX XX XX', placeholder: '20 12 34 56', minLength: 8, maxLength: 8, example: '20123456' },
  { code: 'FI', dial: '+358', name: 'Finland', flag: '🇫🇮', format: 'XX XXX XXXX', placeholder: '40 123 4567', minLength: 9, maxLength: 10, example: '401234567' },
  { code: 'PL', dial: '+48', name: 'Poland', flag: '🇵🇱', format: 'XXX XXX XXX', placeholder: '512 345 678', minLength: 9, maxLength: 9, example: '512345678' },
  { code: 'CZ', dial: '+420', name: 'Czech Republic', flag: '🇨🇿', format: 'XXX XXX XXX', placeholder: '601 123 456', minLength: 9, maxLength: 9, example: '601123456' },
  { code: 'GR', dial: '+30', name: 'Greece', flag: '🇬🇷', format: 'XXX XXX XXXX', placeholder: '691 234 5678', minLength: 10, maxLength: 10, example: '6912345678' },
  { code: 'IE', dial: '+353', name: 'Ireland', flag: '🇮🇪', format: 'XX XXX XXXX', placeholder: '85 123 4567', minLength: 9, maxLength: 9, example: '851234567' },
  { code: 'RO', dial: '+40', name: 'Romania', flag: '🇷🇴', format: 'XXX XXX XXX', placeholder: '721 234 567', minLength: 9, maxLength: 9, example: '721234567' },
  { code: 'HU', dial: '+36', name: 'Hungary', flag: '🇭🇺', format: 'XX XXX XXXX', placeholder: '20 123 4567', minLength: 9, maxLength: 9, example: '201234567' },
  { code: 'RU', dial: '+7', name: 'Russia', flag: '🇷🇺', format: 'XXX XXX-XX-XX', placeholder: '912 345-67-89', minLength: 10, maxLength: 10, example: '9123456789' },
  { code: 'UA', dial: '+380', name: 'Ukraine', flag: '🇺🇦', format: 'XX XXX XX XX', placeholder: '50 123 45 67', minLength: 9, maxLength: 9, example: '501234567' },
  { code: 'TR', dial: '+90', name: 'Turkey', flag: '🇹🇷', format: 'XXX XXX XX XX', placeholder: '532 123 45 67', minLength: 10, maxLength: 10, example: '5321234567' },
  
  // Asia Pacific
  { code: 'JP', dial: '+81', name: 'Japan', flag: '🇯🇵', format: 'XX-XXXX-XXXX', placeholder: '90-1234-5678', minLength: 10, maxLength: 10, example: '9012345678' },
  { code: 'CN', dial: '+86', name: 'China', flag: '🇨🇳', format: 'XXX XXXX XXXX', placeholder: '131 2345 6789', minLength: 11, maxLength: 11, example: '13123456789' },
  { code: 'IN', dial: '+91', name: 'India', flag: '🇮🇳', format: 'XXXXX XXXXX', placeholder: '98765 43210', minLength: 10, maxLength: 10, example: '9876543210' },
  { code: 'KR', dial: '+82', name: 'South Korea', flag: '🇰🇷', format: 'XX-XXXX-XXXX', placeholder: '10-1234-5678', minLength: 9, maxLength: 10, example: '1012345678' },
  { code: 'AU', dial: '+61', name: 'Australia', flag: '🇦🇺', format: 'XXX XXX XXX', placeholder: '412 345 678', minLength: 9, maxLength: 9, example: '412345678' },
  { code: 'NZ', dial: '+64', name: 'New Zealand', flag: '🇳🇿', format: 'XX XXX XXXX', placeholder: '21 123 4567', minLength: 8, maxLength: 9, example: '211234567' },
  { code: 'SG', dial: '+65', name: 'Singapore', flag: '🇸🇬', format: 'XXXX XXXX', placeholder: '9123 4567', minLength: 8, maxLength: 8, example: '91234567' },
  { code: 'MY', dial: '+60', name: 'Malaysia', flag: '🇲🇾', format: 'XX-XXX XXXX', placeholder: '12-345 6789', minLength: 9, maxLength: 10, example: '123456789' },
  { code: 'TH', dial: '+66', name: 'Thailand', flag: '🇹🇭', format: 'XX XXX XXXX', placeholder: '81 234 5678', minLength: 9, maxLength: 9, example: '812345678' },
  { code: 'ID', dial: '+62', name: 'Indonesia', flag: '🇮🇩', format: 'XXX-XXXX-XXXX', placeholder: '812-3456-7890', minLength: 9, maxLength: 12, example: '81234567890' },
  { code: 'PH', dial: '+63', name: 'Philippines', flag: '🇵🇭', format: 'XXX XXX XXXX', placeholder: '917 123 4567', minLength: 10, maxLength: 10, example: '9171234567' },
  { code: 'VN', dial: '+84', name: 'Vietnam', flag: '🇻🇳', format: 'XX XXX XX XX', placeholder: '91 234 56 78', minLength: 9, maxLength: 10, example: '912345678' },
  { code: 'HK', dial: '+852', name: 'Hong Kong', flag: '🇭🇰', format: 'XXXX XXXX', placeholder: '9123 4567', minLength: 8, maxLength: 8, example: '91234567' },
  { code: 'TW', dial: '+886', name: 'Taiwan', flag: '🇹🇼', format: 'XXX XXX XXX', placeholder: '912 345 678', minLength: 9, maxLength: 9, example: '912345678' },
  { code: 'PK', dial: '+92', name: 'Pakistan', flag: '🇵🇰', format: 'XXX XXXXXXX', placeholder: '300 1234567', minLength: 10, maxLength: 10, example: '3001234567' },
  { code: 'BD', dial: '+880', name: 'Bangladesh', flag: '🇧🇩', format: 'XXXX-XXXXXX', placeholder: '1712-345678', minLength: 10, maxLength: 10, example: '1712345678' },
  
  // Middle East & Africa
  { code: 'AE', dial: '+971', name: 'United Arab Emirates', flag: '🇦🇪', format: 'XX XXX XXXX', placeholder: '50 123 4567', minLength: 9, maxLength: 9, example: '501234567' },
  { code: 'SA', dial: '+966', name: 'Saudi Arabia', flag: '🇸🇦', format: 'XX XXX XXXX', placeholder: '50 123 4567', minLength: 9, maxLength: 9, example: '501234567' },
  { code: 'IL', dial: '+972', name: 'Israel', flag: '🇮🇱', format: 'XX-XXX-XXXX', placeholder: '50-123-4567', minLength: 9, maxLength: 9, example: '501234567' },
  { code: 'EG', dial: '+20', name: 'Egypt', flag: '🇪🇬', format: 'XXX XXX XXXX', placeholder: '100 123 4567', minLength: 10, maxLength: 10, example: '1001234567' },
  { code: 'ZA', dial: '+27', name: 'South Africa', flag: '🇿🇦', format: 'XX XXX XXXX', placeholder: '71 123 4567', minLength: 9, maxLength: 9, example: '711234567' },
  { code: 'NG', dial: '+234', name: 'Nigeria', flag: '🇳🇬', format: 'XXX XXX XXXX', placeholder: '803 123 4567', minLength: 10, maxLength: 10, example: '8031234567' },
  { code: 'KE', dial: '+254', name: 'Kenya', flag: '🇰🇪', format: 'XXX XXXXXX', placeholder: '712 345678', minLength: 9, maxLength: 9, example: '712345678' },
  { code: 'MA', dial: '+212', name: 'Morocco', flag: '🇲🇦', format: 'XX-XXXXXXX', placeholder: '61-2345678', minLength: 9, maxLength: 9, example: '612345678' },
  { code: 'QA', dial: '+974', name: 'Qatar', flag: '🇶🇦', format: 'XXXX XXXX', placeholder: '3312 3456', minLength: 8, maxLength: 8, example: '33123456' },
  { code: 'KW', dial: '+965', name: 'Kuwait', flag: '🇰🇼', format: 'XXXX XXXX', placeholder: '5012 3456', minLength: 8, maxLength: 8, example: '50123456' },
];

// Legacy DIAL_CODES export for backwards compatibility
export const DIAL_CODES = COUNTRY_PHONE_CONFIGS.map(c => ({
  code: c.code,
  dial: c.dial,
  label: `${c.name} (${c.dial})`
}));

// Get phone config by country code
export function getPhoneConfig(countryCode: string): CountryPhoneConfig | undefined {
  return COUNTRY_PHONE_CONFIGS.find(c => c.code === countryCode);
}

// Get phone config by dial code
export function getPhoneConfigByDial(dial: string): CountryPhoneConfig | undefined {
  return COUNTRY_PHONE_CONFIGS.find(c => c.dial === dial);
}

/**
 * Format a phone number according to country-specific format
 * @param digits - Raw digits only (no formatting characters)
 * @param countryCode - ISO country code (e.g., 'US')
 * @returns Formatted phone string
 */
export function formatPhoneNumber(digits: string, countryCode: string): string {
  const config = getPhoneConfig(countryCode);
  if (!config) return digits;
  
  // Remove non-digits
  const cleanDigits = digits.replace(/\D/g, '');
  if (!cleanDigits) return '';
  
  // Apply format pattern
  let formatted = '';
  let digitIndex = 0;
  
  for (const char of config.format) {
    if (digitIndex >= cleanDigits.length) break;
    
    if (char === 'X') {
      formatted += cleanDigits[digitIndex];
      digitIndex++;
    } else {
      formatted += char;
    }
  }
  
  // Append any remaining digits
  if (digitIndex < cleanDigits.length) {
    formatted += cleanDigits.slice(digitIndex);
  }
  
  return formatted;
}

/**
 * Validate a phone number for a specific country
 * @param digits - Raw digits only
 * @param countryCode - ISO country code
 * @returns Object with isValid flag and optional error message
 */
export function validatePhoneNumber(digits: string, countryCode: string): { isValid: boolean; error?: string } {
  const config = getPhoneConfig(countryCode);
  if (!config) {
    return { isValid: true }; // Allow if no config found
  }
  
  const cleanDigits = digits.replace(/\D/g, '');
  
  if (!cleanDigits) {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  if (cleanDigits.length < config.minLength) {
    return { 
      isValid: false, 
      error: `Phone number must be at least ${config.minLength} digits for ${config.name}` 
    };
  }
  
  if (cleanDigits.length > config.maxLength) {
    return { 
      isValid: false, 
      error: `Phone number must be at most ${config.maxLength} digits for ${config.name}` 
    };
  }
  
  return { isValid: true };
}

/**
 * Convert a phone number to E.164 format
 * @param raw - Raw phone input (may include formatting)
 * @param countryDial - Country dial code (e.g., '+1')
 * @returns E.164 formatted string or null if invalid
 */
export function normalizeE164(raw: string, countryDial = '+1'): string | null {
  const s = String(raw || '').trim();
  if (!s) return null;

  // Already includes +: strip to digits and return
  if (s.startsWith('+')) {
    const digits = s.slice(1).replace(/\D/g, '').replace(/^0+/, '');
    return digits ? `+${digits}` : null;
  }

  const digitsOnly = s.replace(/\D/g, '').replace(/^0+/, '');
  const cc = countryDial.replace('+', '');
  let digits = digitsOnly;

  // If user typed country code already, don't prefix again
  if (digits.startsWith(cc)) {
    return `+${digits}`;
  }

  // US/Canada trunk handling: users often type 1XXXXXXXXXX. For +1, drop leading trunk '1'.
  if (cc === '1' && digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  return digits ? `+${cc}${digits}` : null;
}

/**
 * Parse digits from a formatted phone string
 * @param formatted - Formatted phone string
 * @returns Raw digits only
 */
export function parsePhoneDigits(formatted: string): string {
  return (formatted || '').replace(/\D/g, '');
}

/**
 * Get display format for a country dial code
 * @param dial - Country dial code (e.g., '+1')
 * @returns Display label with flag
 */
export function getDialCodeDisplay(dial: string): string {
  const config = getPhoneConfigByDial(dial);
  if (!config) return dial;
  return `${config.flag} ${config.dial}`;
}

// Phone input change handler that applies formatting
export function handlePhoneInputChange(
  value: string,
  countryCode: string,
  onChange: (formatted: string, digits: string) => void
): void {
  const digits = parsePhoneDigits(value);
  const formatted = formatPhoneNumber(digits, countryCode);
  onChange(formatted, digits);
}
